import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2, Home, Building2, Calculator, Info } from 'lucide-react';
import { pricing, apiEstimates } from '../../lib/api';
import { fmt, fmtDateUS } from '../../lib/invoiceItems';
import { accountPropertyType } from '../../lib/propertyType';
import { toast } from 'sonner';

const FREQS = ['one-time', 'monthly', 'bi-monthly', 'quarterly', 'annually'];
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function SmartEstimateDialog({ open, setOpen, customers, onSave }) {
  const [rules, setRules] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [propertyType, setPropertyType] = useState('residential');
  const [sqft, setSqft] = useState(2000);
  const [frequency, setFrequency] = useState('quarterly');
  const [picked, setPicked] = useState(() => new Set());
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [typeOverridden, setTypeOverridden] = useState(false);

  // What this account says it is - or null when the record does not say. The
  // toggle used to open on "residential" every time regardless of who was
  // selected, so pricing a commercial property at house rates took no mistake,
  // only forgetting to change it.
  const accountType = accountPropertyType(customers.find((c) => c.id === customerId));

  useEffect(() => {
    if (open) {
      pricing.rules().then((r) => setRules(r || [])).catch(() => setRules([]));
      setCustomerId(customers[0]?.id || ''); setPicked(new Set()); setSqft(2000); setFrequency('quarterly');
    }
  }, [open, customers]);

  // Follow the selected account. Also clears any calculated price: the figures
  // on screen belong to the customer they were worked out for, and once the
  // property type follows the customer, keeping them would let a quote be
  // created at the previous account's rate.
  useEffect(() => {
    if (!open) return;
    setTypeOverridden(false);
    setResult(null);
    setPropertyType(accountType || 'residential');
  }, [open, customerId, accountType]);

  const toggle = (st) => setPicked((s) => { const n = new Set(s); n.has(st) ? n.delete(st) : n.add(st); setResult(null); return n; });

  const calculate = async () => {
    if (picked.size === 0) { toast.error('Pick at least one service'); return; }
    setBusy(true);
    try {
      const r = await pricing.suggest({ serviceTypes: Array.from(picked), propertyType, sqft: Number(sqft) || 0, frequency });
      setResult(r);
    } catch { toast.error('Could not calculate'); }
    finally { setBusy(false); }
  };

  const createEstimate = async () => {
    if (!result) return;
    if (!customerId) { toast.error('Choose a customer'); return; }
    const cust = customers.find((c) => c.id === customerId);
    const items = result.lines.map((l) => ({
      desc: `${l.serviceType}${l.oneTime ? '' : ` (${frequency})`} — ${propertyType}, ${sqft} sq ft`,
      qty: 1, rate: l.firstVisit,
    }));
    try {
      await apiEstimates.create({
        id: `EST-${Date.now().toString(36)}`, customerId, customerName: cust?.name || '',
        date: fmtDateUS(new Date()), expires: fmtDateUS(new Date(Date.now() + 30 * 86400000)),
        status: 'Pending', items, total: result.firstVisitTotal,
        meta: { smartEstimate: true, frequency, annual: result.annualTotal, perVisit: result.perVisitTotal },
      });
      onSave(); setOpen(false); toast.success('Smart estimate created');
    } catch { toast.error('Could not create estimate'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="smart-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#8BC53F]" />Smart Estimate</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1.5" data-testid="se-customer"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent className="max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service frequency</Label>
              <Select value={frequency} onValueChange={(v) => { setFrequency(v); setResult(null); }}>
                <SelectTrigger className="mt-1.5" data-testid="se-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Property type</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[['residential', Home], ['commercial', Building2]].map(([pt, Ico]) => (
                  <button key={pt} onClick={() => { setPropertyType(pt); setTypeOverridden(true); setResult(null); }} data-testid={`se-prop-${pt}`}
                    className={`flex items-center justify-center gap-1.5 h-10 rounded-md border text-sm font-semibold capitalize ${propertyType === pt ? 'border-[#8BC53F] bg-[#8BC53F]/10 text-[#6FA82C]' : 'border-slate-200 text-slate-600'}`}>
                    <Ico className="w-4 h-4" />{pt}
                  </button>
                ))}
              </div>
              {/* Where this value came from, always. A prefill nobody can see is
                  worse than no prefill: it is a number on a quote that no one
                  remembers choosing. */}
              {customerId && !accountType && (
                <p className="mt-1.5 text-xs text-amber-700 flex items-start gap-1" data-testid="se-prop-note">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  This account has no property type set — check before quoting.
                </p>
              )}
              {customerId && accountType && typeOverridden && propertyType !== accountType && (
                <p className="mt-1.5 text-xs text-amber-700 flex items-start gap-1" data-testid="se-prop-note">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  The account is set to {accountType} — this quote overrides it.
                </p>
              )}
              {customerId && accountType && propertyType === accountType && (
                <p className="mt-1.5 text-xs text-slate-500 flex items-start gap-1" data-testid="se-prop-note">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  From the customer record.
                </p>
              )}
            </div>
            <div>
              <Label>Square footage</Label>
              <Input type="number" value={sqft} onChange={(e) => { setSqft(e.target.value); setResult(null); }} className="mt-1.5" data-testid="se-sqft" />
            </div>
          </div>

          <div>
            <Label>Services / pests</Label>
            <div className="mt-1.5 grid sm:grid-cols-2 gap-2">
              {rules.map((r) => (
                <label key={r.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${picked.has(r.serviceType) ? 'border-[#8BC53F] bg-[#8BC53F]/5' : 'border-slate-200'}`} data-testid={`se-service-${r.serviceType.replace(/\s+/g, '-')}`}>
                  <input type="checkbox" checked={picked.has(r.serviceType)} onChange={() => toggle(r.serviceType)} className="w-4 h-4 accent-[#8BC53F]" />
                  <span className="text-slate-700">{r.serviceType}</span>
                </label>
              ))}
            </div>
          </div>

          <Button onClick={calculate} disabled={busy} variant="outline" className="w-full gap-2 border-[#8BC53F] text-[#6FA82C]" data-testid="se-calculate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}Suggest pricing
          </Button>

          {result && (
            <div className="rounded-xl border border-[#8BC53F]/40 bg-[#8BC53F]/5 p-4 space-y-3" data-testid="se-result">
              <div className="divide-y divide-slate-200">
                {result.lines.map((l) => (
                  <div key={l.serviceType} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-slate-700">{l.serviceType}{l.oneTime ? <span className="ml-1 text-[10px] uppercase font-bold text-amber-600">one-time</span> : null}</span>
                    <span className="font-semibold text-slate-900">{money(l.firstVisit)}{!l.oneTime && <span className="text-slate-400 font-normal"> first · {money(l.perVisit)}/visit</span>}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div className="text-center"><div className="text-[11px] text-slate-500 uppercase">First visit</div><div className="text-lg font-extrabold text-slate-900" data-testid="se-first-total">{money(result.firstVisitTotal)}</div></div>
                <div className="text-center"><div className="text-[11px] text-slate-500 uppercase">Per visit</div><div className="text-lg font-extrabold text-slate-900">{money(result.perVisitTotal)}</div></div>
                <div className="text-center"><div className="text-[11px] text-slate-500 uppercase">Est. annual</div><div className="text-lg font-extrabold text-[#6FA82C]" data-testid="se-annual-total">{money(result.annualTotal)}</div></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={createEstimate} disabled={!result} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="se-create-btn">Create Estimate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
